// AI-CONTEXT: See docs/features/participatieladder-2026-3.md
import computeIncomeTax from './computeIncomeTax'
import attributedValue from '../msiMonetisationHelpers/applyRekenregels'

// Rekenmodel van parameterset 2026.3 (meetstandaard-API), het model van de
// MSI-kennisbank dat sinds 2026-10-20 leidend is: per stakeholder en per post
// het verschil tussen 0- en 1-meting, met soort waarde en overdracht uit
// `parameters.posts`. Bron: `calculation.steps` van de set, en de
// referentie-implementatie situatie()/vrije_tijd()/posten() in
// meetstandaard-api/functions/arbeidsparticipatie.test.js, die
// bouw_outputmeting.py van de kennisbank volgt.
//
// De bedragen van het model zijn BRUTO jaarbedragen. Met een `horizon` komt
// daarnaast de toegerekende waarde van de MSI-rekenregels (impactclaim,
// tijdshorizon, drop-off, disconto) op elke post en op de totalen — besluit
// kennisbank 2026-10-22, docs/features/msi-rekenregels-monetarisering.md.
//
// Spiegel: functions/participationHelpers/calculateParticipationPosts.js,
// bewaakt door calculateParticipationPosts.parity.test.js.

/**
 * @typedef {object} SituationInput - De invoer van het rekenmodel, zoals situatie() in de kennisbank
 * @property {string|null} werk - Urenklasse bij werk, anders null
 * @property {string} opleiding - Hoogst afgeronde opleiding
 * @property {number} inzetbaar - Inzetbaarheidsfactor (1 = volledig)
 * @property {string|null} uitkering - benefitType van 2026.3, null bij geen
 * @property {number} vrijwilligersuren - Uren per week
 * @property {boolean} lks - Loonkostensubsidie
 * @property {string|null} dagbesteding - Wmo / Jeugdwet / Wlz, null bij geen
 * @property {number} dagdelen - Dagdelen per week
 * @property {string|null} opleidingDoel - Doelniveau van de opleiding die de deelnemer volgt
 */

/**
 * @typedef {object} Situation - Bedragen van één meetmoment
 * @property {number} loon
 * @property {number} uitkering - Ontvangen uitkering (positief)
 * @property {string|null} uitkeringPayer
 * @property {number} belasting
 * @property {number} deelnemerNetto
 * @property {number} uitvoering - Uitvoeringskosten bijstand (positief)
 * @property {number} lks - Loonkostensubsidie (positief)
 * @property {number} dagbesteding - Kosten dagbesteding per jaar (positief)
 * @property {string|null} dagbestedingPayer
 * @property {number} vrijwilligerswerk
 * @property {number} loonwinst
 */

/**
 * @typedef {object} PostRow
 * @property {string} stakeholder
 * @property {string} post
 * @property {string} typeWaardering - direct financieel / indirect financieel / niet-financieel
 * @property {boolean} overdracht
 * @property {number} value - Verschil 1-meting − 0-meting in euro's per jaar
 * @property {number|null} [attributed] - Toegerekende waarde (rekenregels); null zonder horizon
 */

const PAID_WORK = 'Werk'
const DEFAULT_EDUCATION = 'Geen startkwalificatie' // zoals situatie() in de kennisbank
const GOVERNMENT = ['Rijk', 'Gemeente', 'UWV', 'SVB']
const TYPES = ['direct financieel', 'indirect financieel', 'niet-financieel']

const num = (value) => (Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value) : null)

/**
 * Het uitkeringstype van 2026.3, met oude antwoorden omgezet
 * (`benefitRules.legacyBenefitType`). Onbekend of Geen → null.
 * @param {string|undefined} benefitType
 * @param {object} p - parameters
 * @returns {string|null}
 */
const benefitTypeOf = (benefitType, p) => {
    const legacy = (p.benefitRules && p.benefitRules.legacyBenefitType) || {}
    const type = legacy[benefitType] || benefitType
    return type && type !== 'Geen' && Number.isFinite(p.benefitAmountPerYear[type]) ? type : null
}

/**
 * Vertaalt een opgeslagen profiel (het formulier) naar de invoer van het rekenmodel.
 * @param {object} profile
 * @param {object} p - parameters 2026.3
 * @returns {SituationInput}
 */
const situationInputFromProfile = (profile = {}, p) => {
    const isWork = profile.mainActivity === PAID_WORK
    const werk = isWork && profile.hoursBand ? profile.hoursBand : null
    const inzetbaar = num(profile.employabilityFactor)
    const dagbesteding = p.dagbestedingPerDagdeel[profile.dagbesteding] ? profile.dagbesteding : null
    return {
        werk,
        opleiding: profile.educationLevel || DEFAULT_EDUCATION,
        inzetbaar: inzetbaar ?? 1,
        uitkering: benefitTypeOf(profile.benefitType, p),
        vrijwilligersuren: profile.mainActivity === 'Vrijwilligerswerk' ? Math.max(0, num(profile.volunteerHoursPerWeek) ?? 0) : 0,
        lks: Boolean(werk) && profile.loonkostensubsidie === true,
        dagbesteding,
        dagdelen: dagbesteding ? Math.max(0, num(profile.dagdelenPerWeek) ?? 0) : 0,
        opleidingDoel: profile.mainActivity === 'Opleiding' && profile.educationTarget ? profile.educationTarget : null,
    }
}

const algemeneHeffingskorting = (p, inkomen) => {
    const { max, afbouwVanaf, afbouwPercentage } = p.taxCredits.algemeneHeffingskorting
    return Math.max(0, max - Math.max(0, inkomen - afbouwVanaf) * afbouwPercentage)
}

const arbeidskorting = (p, loon) => {
    const schijf = p.taxCredits.arbeidskorting.find(({ upTo }) => upTo === null || loon <= upTo)
    return Math.max(0, schijf.base + schijf.rate * (loon - schijf.from))
}

const grossIncome = (p, opleiding, band) => {
    const value = (p.incomeByEducationAndHoursBand[opleiding] || {})[band]
    return Number.isFinite(value) ? value : 0
}

// Verwachte loonwinst: slaagkans × (loon op het doelniveau − loon nu), bij
// `wageGainFromEducation.hoursBand`; alleen naar een hoger niveau.
const wageGain = (p, opleiding, doel) => {
    if (!doel) return 0
    const { targetLevel, successRate, hoursBand } = p.wageGainFromEducation
    const niveau = targetLevel[doel]
    const levels = p.options.educationLevel
    if (!niveau || levels.indexOf(niveau) <= levels.indexOf(opleiding)) return 0
    return (successRate[niveau] || 0) * (grossIncome(p, niveau, hoursBand) - grossIncome(p, opleiding, hoursBand))
}

/**
 * Bedragen van één meetmoment (`calculation.steps`).
 * @param {object} p - parameters 2026.3
 * @param {SituationInput} input
 * @returns {Situation}
 */
const computeSituation = (p, input) => {
    const {
        werk, opleiding = DEFAULT_EDUCATION, inzetbaar = 1, uitkering, vrijwilligersuren = 0,
        lks = false, dagbesteding, dagdelen = 0, opleidingDoel,
    } = input
    const loon = werk ? grossIncome(p, opleiding, werk) * inzetbaar : 0
    let uit = uitkering ? -p.benefitAmountPerYear[uitkering] : 0
    // Wijziging 3b: naast werk alleen de aanvulling tot het uitkeringsniveau.
    if (werk && uit) uit = Math.max(0, uit - loon)
    const totaal = loon + uit
    const belasting = Math.max(
        0,
        computeIncomeTax(totaal, p.taxBrackets) - algemeneHeffingskorting(p, totaal) - (loon ? arbeidskorting(p, loon) : 0),
    )
    const dag = dagbesteding ? p.dagbestedingPerDagdeel[dagbesteding] : null
    return {
        loon,
        uitkering: uit,
        uitkeringPayer: uit ? p.benefitPayer[uitkering] : null,
        belasting,
        deelnemerNetto: loon + uit - belasting,
        uitvoering: uit && uitkering === 'Bijstandsuitkering' ? -p.bijstandUitvoeringskostenPerYear : 0,
        lks: lks ? -p.loonkostensubsidiePerYear : 0,
        dagbesteding: dag ? -dag.amount * dagdelen * p.weeksPerYear : 0,
        dagbestedingPayer: dag ? dag.payer : null,
        vrijwilligerswerk: vrijwilligersuren * p.weeksPerYear * p.volunteerValuePerHour,
        loonwinst: wageGain(p, opleiding, opleidingDoel),
    }
}

// Opgegeven vrije tijd (Werkwijzer MKBA sociaal domein, `freeTime`).
const freeTime = (p, a, b) => {
    const dLoon = b.loon - a.loon
    if (Math.abs(dLoon) < 0.5) return 0
    const f = !a.loon && dLoon > 0 ? p.freeTime.factorEntrant : p.freeTime.factorExtraHours
    const dUitkering = a.uitkering - b.uitkering
    const dNetto = b.deelnemerNetto - a.deelnemerNetto + dUitkering
    return dLoon > 0 ? -Math.max(0, f * dNetto - dUitkering) : Math.max(0, f * -dNetto + dUitkering)
}

// Soort waarde en overdracht uit `parameters.posts`. De stakeholders
// 'benefitPayer' en 'dagbestedingPayer' zijn plaatshouders voor de betaler.
const postMeta = (p, stakeholder, post) => {
    const kandidaten = p.posts.filter((x) => x.post === post)
    return kandidaten.find((x) => x.stakeholder === stakeholder)
        || kandidaten.find((x) => !p.calculation.stakeholders.includes(x.stakeholder))
}

/**
 * Het verschil per stakeholder en per post (`posts`). Posten onder een halve
 * euro vallen weg, zoals in het rekenmodel.
 * @param {object} p - parameters 2026.3
 * @param {Situation} a - 0-meting
 * @param {Situation} b - 1-meting
 * @returns {Array<PostRow>}
 */
const computePosts = (p, a, b) => {
    const rijen = [
        ['Deelnemer', 'Bruto loon', b.loon - a.loon],
        ['Deelnemer', 'Verwachte loonwinst na opleiding', b.loonwinst - a.loonwinst],
        ['Deelnemer', 'Belasting en premies', -(b.belasting - a.belasting)],
        ['Deelnemer', 'Uitkering', b.uitkering - a.uitkering],
        ['Deelnemer', 'Opgegeven vrije tijd', freeTime(p, a, b)],
        ['Rijk', 'Belasting en premies', b.belasting - a.belasting],
    ]
    // Een overdracht per betaler: wie bij de 0-meting betaalde, bespaart; wie
    // bij de 1-meting betaalt, betaalt.
    const perPayer = (veld, payer) => {
        const totaal = new Map()
        ;[[a, 1], [b, -1]].forEach(([m, teken]) => {
            if (m[payer]) totaal.set(m[payer], (totaal.get(m[payer]) || 0) + teken * m[veld])
        })
        return [...totaal]
    }
    perPayer('uitkering', 'uitkeringPayer').forEach(([sh, w]) => rijen.push([sh, 'Uitkering', w]))
    rijen.push(['Gemeente', 'Uitvoeringskosten bijstand', a.uitvoering - b.uitvoering])
    rijen.push(['Gemeente', 'Loonkostensubsidie', a.lks - b.lks])
    rijen.push(['Werkgever', 'Loonkostensubsidie', b.lks - a.lks])
    perPayer('dagbesteding', 'dagbestedingPayer').forEach(([sh, w]) => rijen.push([sh, 'Dagbesteding', w]))
    rijen.push([p.volunteerValueStakeholder, 'Vrijwilligerswerk', b.vrijwilligerswerk - a.vrijwilligerswerk])

    return rijen
        .filter(([, , w]) => Math.abs(w) >= 0.5)
        .map(([stakeholder, post, value]) => {
            const meta = postMeta(p, stakeholder, post) || {}
            return { stakeholder, post, typeWaardering: meta.typeWaardering, overdracht: Boolean(meta.overdracht), value }
        })
}

/**
 * Leest een geboortedatum zoals hij in Gegevens staat: jjjj-mm-dd of
 * dd-mm-jjjj (ook met / of .). Onleesbaar → null.
 * @param {string|undefined|null} value
 * @returns {Date|null}
 */
const parseBirthDate = (value) => {
    if (!value || typeof value !== 'string') return null
    const iso = value.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    const nl = value.trim().match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/)
    const [y, m, d] = iso ? [iso[1], iso[2], iso[3]] : nl ? [nl[3], nl[2], nl[1]] : []
    if (!y) return null
    const date = new Date(Number(y), Number(m) - 1, Number(d))
    return date.getMonth() === Number(m) - 1 && date.getDate() === Number(d) ? date : null
}

const ageAt = (born, date) => {
    let age = date.getFullYear() - born.getFullYear()
    const beforeBirthday = date.getMonth() < born.getMonth()
        || (date.getMonth() === born.getMonth() && date.getDate() < born.getDate())
    return beforeBirthday ? age - 1 : age
}

const toDate = (value) => {
    if (value instanceof Date) return value
    if (value && typeof value.toDate === 'function') return value.toDate()
    if (Number.isFinite(value)) return new Date(value)
    return null
}

/**
 * Leeftijdsovergang (`calculation.ageTransition`): passeert de deelnemer
 * tussen de metingen de leeftijdsgrens van de uitkering van de 1-meting, dan
 * krijgt ook de 0-meting die uitkering — die was er zonder de interventie ook
 * gekomen.
 * @returns {{ input0: SituationInput, notices: Array<string> }}
 */
const applyAgeTransition = (p, input0, input1, context = {}) => {
    const notices = []
    const born = parseBirthDate(context.birthDate)
    if (!context.birthDate) notices.push('geboortedatumOntbreekt')
    else if (!born) notices.push('geboortedatumOnleesbaar')

    const t0 = toDate(context.t0Date)
    const t1 = toDate(context.t1Date)
    if (!born || !t0 || !t1 || !input1.uitkering || input0.uitkering === input1.uitkering) {
        return { input0, notices }
    }
    const limits = p.calculation.ageTransition.ageLimits
    const limit = limits[input1.uitkering] ?? limits.default
    if (ageAt(born, t0) < limit && ageAt(born, t1) >= limit) {
        return { input0: { ...input0, uitkering: input1.uitkering }, notices: [...notices, 'leeftijdsovergang'] }
    }
    return { input0, notices }
}

/**
 * Zet op elke post de toegerekende waarde (× impactclaim × factor van de
 * horizon). De factor is lineair, dus een overdracht valt ook toegerekend weg.
 * @param {Array<PostRow>} posts
 * @param {number|null|undefined} horizon - Tijdshorizon in jaren; null = niet doorrekenen
 * @returns {Array<PostRow>}
 */
const attributePosts = (posts, horizon) => posts.map((row) => ({ ...row, attributed: attributedValue(row.value, horizon) }))

const sumBy = (rows, key) => rows.reduce((acc, row) => ({ ...acc, [row[key]]: (acc[row[key]] || 0) + row.value }), {})

// Toegerekende totalen: dezelfde rekenregel op elk totaal (lineair, dus gelijk
// aan de som van de toegerekende posten). null zonder horizon.
const attributeTotals = (totals, horizon) => {
    if (attributedValue(0, horizon) === null) return null
    return {
        horizon,
        reeleWaarde: attributedValue(totals.reeleWaarde, horizon),
        governmentBalance: attributedValue(totals.governmentBalance, horizon),
        personalIncomeChange: attributedValue(totals.personalIncomeChange, horizon),
    }
}

/**
 * @typedef {object} PostsImpact
 * @property {Situation} t0
 * @property {Situation} t1
 * @property {Array<PostRow>} posts
 * @property {Object<string, number>} perStakeholder
 * @property {Object<string, number>} perType - Altijd met de drie soorten, ook als 0
 * @property {{ reeleWaarde: number, governmentBalance: number, personalIncomeChange: number }} totals
 * @property {{ horizon: number, reeleWaarde: number, governmentBalance: number, personalIncomeChange: number }|null} attributedTotals - Dezelfde totalen toegerekend; null zonder horizon
 * @property {Array<string>} notices - geboortedatumOntbreekt | geboortedatumOnleesbaar | leeftijdsovergang
 */

/**
 * @param {object} input
 * @param {object} input.profileT0
 * @param {object} input.profileT1
 * @param {object} input.parameters - parameters 2026.3 (taxBrackets met Infinity)
 * @param {{ birthDate?: string, t0Date?: *, t1Date?: * }} [input.context] - Voor de leeftijdsovergang
 * @param {number|null} [input.horizon] - Tijdshorizon van de rekenregels (resolveInterventionDose); null = niet doorrekenen
 * @returns {PostsImpact}
 */
const calculateParticipationPosts = ({ profileT0 = {}, profileT1 = {}, parameters: p, context = {}, horizon = null }) => {
    const input1 = situationInputFromProfile(profileT1, p)
    const { input0, notices } = applyAgeTransition(p, situationInputFromProfile(profileT0, p), input1, context)
    const t0 = computeSituation(p, input0)
    const t1 = computeSituation(p, input1)
    const posts = attributePosts(computePosts(p, t0, t1), horizon)

    const perType = { ...Object.fromEntries(TYPES.map((t) => [t, 0])), ...sumBy(posts, 'typeWaardering') }
    const sum = (rows) => rows.reduce((s, r) => s + r.value, 0)
    const totals = {
        // Overdrachten vallen in de som weg: dit is de reële waarde.
        reeleWaarde: sum(posts),
        governmentBalance: sum(posts.filter((r) => GOVERNMENT.includes(r.stakeholder))),
        // Wat de deelnemer er financieel op vooruitgaat: bruto loon − belasting − weggevallen uitkering.
        personalIncomeChange: sum(posts.filter((r) => r.stakeholder === 'Deelnemer' && r.typeWaardering === 'direct financieel')),
    }
    return {
        t0,
        t1,
        posts,
        perStakeholder: sumBy(posts, 'stakeholder'),
        perType,
        totals,
        attributedTotals: attributeTotals(totals, horizon),
        notices,
    }
}

export default calculateParticipationPosts
export { computeSituation, computePosts, situationInputFromProfile, parseBirthDate, attributePosts }
